// Shared by the Selected collection and the AI View catalog.
export const selectedResearchCount = 4;

// Structured source for every publication on the Research page. The
// PublicationItem module renders each object, so adding a pub means adding
// one entry here; the narrative text lives in publicationDescriptions and is
// keyed by the same id.

const lab = {
  slr: {
    name: 'SLR Lab',
    url: 'https://slrlab.edu.hku.hk/',
    logo: '/assets/img/slr.png',
    logoHeight: '11vh'
  },
  xlang: {
    name: 'XLANG Lab',
    url: 'https://www.xlang.ai/',
    logo: '/assets/img/xlang.png',
    logoHeight: '6vh'
  }
};

export const researchPublications = [
  {
    id: 'transfer',
    group: 'human',
    priority: 4,
    title: 'The Resolving Power of Transfer Experiments: When Can Designs Distinguish Accounts of Generalization?',
    preview: 'A framework for constructing models of human generalisation from cognitive mechanisms and using their predictions to guide experimental design.',
    text: 'transfer experiments generalization resolving power candidate space representation geometry experience policy constructive framework candidate separation recovery tide experimental design revision computational models SLR Lab human learning',
    period: 'September 2025 - June 2026',
    status: 'Under review',
    lab: lab.slr,
    authors: 'Han-yu Wang, Stephen M. K. Lee',
    self: 'Han-yu Wang',
    links: [
      {
        label: 'Explore transfer designs',
        url: 'https://huggingface.co/spaces/project-tide/tide-audit',
        presentation: 'resource',
        iconSrc: '/assets/img/huggingface.svg'
      }
    ],
    images: ['fig1_overview', 'fig2_machine', 'fig3_structure', 'fig_plane'].map(name => `/assets/img/research/tide-20260909/${name}.svg`),
    figureLabels: ['Framework', 'Evidence accumulation', 'Task structure', 'Design comparisons']
  },
  {
    id: 'bright',
    group: 'lm',
    priority: 3,
    title: 'BRIGHT: A Realistic and Challenging Benchmark for Reasoning-Intensive Retrieval',
    preview: 'A benchmark of 1,384 real queries that tests retrieval when relevance becomes clear only after reasoning.',
    text: 'BRIGHT benchmark reasoning intensive retrieval XLANG ICLR',
    period: 'December 2023 - May 2024',
    status: 'ICLR 2025 Spotlight',
    lab: lab.xlang,
    authors: 'Hongjin Su*, Howard Yen*, Mengzhou Xia*, Weijia Shi, Niklas Muennighoff, Han-yu Wang, Haisu Liu, Quan Shi, Zachary S. Siegel, Michael Tang, Ruoxi Sun, Jinsung Yoon, Sercan O. Arik, Danqi Chen, Tao Yu',
    self: 'Han-yu Wang',
    links: [
      { label: 'Paper', url: 'https://arxiv.org/abs/2407.12883' },
      { label: 'Project page', url: 'https://brightbenchmark.github.io/' }
    ],
    images: ['BRIGHT1', 'BRIGHT2', 'BRIGHT4', 'BRIGHT5'],
    figureLabels: ['Reasoning and relevance', 'Dataset explorer', 'Domains and sources', 'Retrieval results']
  },
  {
    id: 'fittedAssistance',
    group: 'humanAi',
    priority: 5,
    anchor: 'structure-settles-thought',
    title: 'Early Epistemic Settlement in AI-Assisted Writing',
    preview: 'How an adequate AI answer can redirect inquiry before new connections, arguments and questions take shape.',
    text: 'human AI interaction collaboration AI-assisted writing early epistemic settlement writing as inquiry inquiry state local resolution local sufficiency organisation reach formable foreclosed reach writer-side integration relations constraints possibilities state inheritance',
    period: 'June 2026 - Present',
    status: 'Under review',
    authors: 'Han-yu Wang',
    self: 'Han-yu Wang',
    links: [
      { label: 'Paper', url: 'https://arxiv.org/abs/2609.09332' },
      { label: 'Interview', url: '/research/ai-writing-interview', internal: true, icon: 'interview' }
    ],
    images: []
  },
  {
    id: 'functionVector',
    group: 'lm',
    priority: 3,
    title: 'Function-Vector Heads Are Two Populations: Writers and Cancellers in In-Context Learning',
    preview: 'How attention heads support and suppress correct answers, and why their combined effect can hide both contributions.',
    text: 'function vectors candidate attention heads writers cancellers in-context learning mechanistic interpretability direct logit attribution causal interventions ablation accuracy task-dependent roles opposing contributions',
    period: 'September 2025 - March 2026',
    status: 'Under review',
    authors: 'Han-yu Wang',
    self: 'Han-yu Wang',
    links: [
      { label: 'Paper', url: 'https://arxiv.org/abs/2606.07560' }
    ],
    images: ["/assets/img/research/function-vectors-20261005/fig5_intervention.png", "/assets/img/research/function-vectors-20261005/fig1_headline.png", "/assets/img/research/function-vectors-20261005/fig4_casestudy.png", "/assets/img/research/function-vectors-20261005/fig2_qk_mechanism.png", "/assets/img/research/function-vectors-20261005/fig3_layer_geometry.png"],
    figureLabels: ["Accuracy after removing cancellers", "Opposing effects on predictions", "Source content and changing roles", "What the heads attend to", "Where the two roles occur"],
    figureCaptions: ["Removing cancellers raises accuracy by 2.4 to 6.8 percentage points across two rule families and three Pythia models. Each comparison uses the same 500 held-out prompts.", "Mean ablation lowers the correct-answer logit contrast for writers and raises it for cancellers in all six settings. Replacing both groups measures their combined effect. Error bars mark 95% bootstrap confidence intervals.", "In Pythia-410M, shuffling source values weakens the negative contribution of head L11.H4. Removing the same head reduces support for correct antonyms, showing that its role changes with the task.", "Writers attend more to demonstration labels, while cancellers allocate more attention to the format prefix. Source attribution in the paper links the dominant suppressive contributions to task content.", "Writers and cancellers occupy overlapping layers. Cancellers cluster in early and middle layers, while writers extend further into the model. Larger markers indicate stronger paired intervention effects."]
  },
  {
    id: 'humansDisengage',
    group: 'humanAi',
    priority: 3,
    title: 'Humans Disengage, Reasoning Models Persist: Separating Difficulty Registration from Deliberation Allocation',
    preview: 'How people and reasoning models allocate further effort, viewed through the expected value and cost of continued reasoning.',
    text: 'humans reasoning models comparison deliberation allocation difficulty registration selective persistence control policies resource rational expected progress uncertainty reducibility reasoning traces H-ARC INTUIT Cortes',
    period: 'January 2026 - May 2026',
    status: 'Under review',
    authors: 'Han-yu Wang',
    self: 'Han-yu Wang',
    links: [
      { label: 'Paper', url: 'https://arxiv.org/abs/2606.26502' }
    ],
    images: [1, 2, 3, 4].map(number => `/assets/img/research/disengage-20260909/figure_${number}.svg`),
    figureLabels: ['Difficulty and effort', 'The allocation gap', 'Across reasoning tasks', 'Actions and traces']
  },
  /* Hidden from the research page.
  {
    id: 'priorsPersist',
    group: 'lm',
    priority: 2,
    title: 'Persistent Priors, Preserved Targets: A Stroop-Style Paradigm for Lexical Override',
    preview: 'Tracing how language models maintain a newly assigned meaning while familiar associations continue to compete.',
    text: 'language models persistent priors preserved targets stroop lexical override model internals causal intervention',
    period: 'March 2026 - May 2026',
    status: 'Under review',
    authors: 'Han-yu Wang',
    self: 'Han-yu Wang',
    links: [
      { label: 'Paper', url: 'https://arxiv.org/abs/2606.07555' }
    ],
    images: [
      'priors_fig1_paradigm',
      'priors_fig2_model_forest',
      'priors_fig3_heatmaps',
      'priors_fig5_mechanism_core',
      'priors_fig7_evidence'
    ],
    figureLabels: ['The override task', 'Across models', 'Prompts and conflicts', 'Causal mechanisms', 'Recovering the target']
  },
  */

  /* Hidden from the research page.
  {
    id: 'smoothLearningCurve',
    group: 'human',
    title: 'When Does a Smooth Learning Curve Mean Smooth Learning? Representational Format, Identifiability, and the Bounded Averaging Artifact',
    text: 'smooth learning curve representational format identifiability bounded averaging artifact human statistical learning',
    period: 'February 2026 - June 2026',
    citation: false,
    authors: 'Han-yu Wang',
    self: 'Han-yu Wang',
    links: [],
    images: ['slr_averaging_artifact', 'slr_identifiability', 'slr_empirical']
  },
  */

  {
    id: 'encoding',
    group: 'human',
    priority: 2,
    title: 'Functional Encoding and Representational Binding in Componential Transfer',
    preview: 'Modelling how the encoding and organisation of knowledge shape which learned components a person can reuse.',
    text: 'functional encoding representational binding componential transfer SLR Lab human learning',
    period: 'July 2025 - June 2026',
    status: 'Under review',
    citation: false,
    lab: lab.slr,
    authors: 'Stephen M. K. Lee, Han-yu Wang',
    self: 'Han-yu Wang',
    links: [],
    images: ['binding_fig1', 'binding_fig2', 'binding_fig3', 'binding_fig4', 'binding_fig5'],
    figureLabels: ['Encoding and binding', 'Transfer predictions', 'Task structure', 'Learning and exposure', 'Representational geometry']
  },
];
